import { useEffect, useState } from 'react';
import { call } from './api';
import type { FavTeam } from './sportcenter';

/** Sports Hub: wat je volgt, zenders per wedstrijd, meldingen en samenvattingen (zie server/sporthub.ts). */

export interface HubSide { name: string; short: string; abbr: string; logo: string | null; color: string | null; score: string | null }
export interface HubChannel { accountId: string; streamId: number; name: string; logo: string | null; provider: string; quality: string | null; reason: string; catchup: boolean; fixed?: boolean }
export interface HubCard {
  key: string;
  kind: 'match' | 'event';
  sport: string;
  league: string;
  leagueName: string;
  start: number;
  stop: number | null;
  state: 'pre' | 'in' | 'post';
  detail: string;
  minute: string | null;
  home: HubSide | null;
  away: HubSide | null;
  title: string;
  sub: string | null;
  venue: string | null;
  fav: boolean;
  path: string | null;
  eventId: string | null;
  scorers: { side: 'home' | 'away'; minute: string; player: string }[];
  channels: HubChannel[];
  remind: boolean;
}
export interface HubHome {
  hero: HubCard | null;
  live: HubCard[];
  teams: { team: FavTeam; form: string; matches: HubCard[] }[];
  weekend: HubCard[];
  hideScores: boolean;
  iptv: boolean;
  poll: number;
}
export interface HubVideo {
  id: string; title: string; channel: string; published: number; thumb: string; leagues: string[]; fav: boolean;
  match: { key: string; league: string; home: HubSide; away: HubSide; start: number; scorers: { side: 'home' | 'away'; minute: string; player: string }[] } | null;
}
export interface HubFollow {
  teams: FavTeam[];
  people: string[];
  sports: string[];
  alerts: Record<string, { goal?: boolean; auto?: boolean }>;
  hideScores: boolean;
  /** Sport in de Live TV-speler: uit, alleen de stand, volledig */
  skin: 'off' | 'score' | 'full' | 'custom';
  custom?: { score: boolean; strip: boolean; ticker: boolean; panel: boolean } | null;
  /** Losse onderdelen in de speler */
  parts: { score: boolean; strip: boolean; ticker: boolean; panel: boolean };
  /** Blokken in de lichtkrant, in volgorde */
  ticker: string[];
  tickerChoices: string[];
  sportChoices: { id: string; name: string; sport: string; color: string; abbr: string }[];
}
export interface HubPulse {
  live: { key: string; title: string; home: HubSide; away: HubSide; minute: string | null; detail: string; goal: boolean; channel: HubChannel | null; path: string; eventId: string }[];
  upcoming: { key: string; title: string; start: number; auto: boolean; remind: boolean; channel: HubChannel | null }[];
  hideScores: boolean;
  poll: number;
}

export const hubApi = {
  home: (from: number, to: number) => call<HubHome>(`/api/sporthub/home?from=${from}&to=${to}`),
  channels: (c: Pick<HubCard, 'key' | 'eventId' | 'start' | 'home' | 'away'>) => {
    const q = new URLSearchParams({ key: c.key, start: String(c.start) });
    if (c.eventId) q.set('event', c.eventId);
    if (c.home) q.set('home', c.home.short);
    if (c.away) q.set('away', c.away.short);
    return call<{ channels: HubChannel[]; fixed: boolean }>(`/api/sporthub/channels?${q}`);
  },
  fix: (key: string, ch: { accountId: string; streamId: number; name: string } | null) =>
    call<{ ok: true }>('/api/sporthub/fix', { method: 'POST', body: JSON.stringify(ch ? { key, ...ch } : { key }) }),
  follow: () => call<HubFollow>('/api/sporthub/follow'),
  saveFollow: (b: Partial<Pick<HubFollow, 'people' | 'sports' | 'alerts' | 'hideScores' | 'skin' | 'parts' | 'ticker'>>) => call<Partial<HubFollow>>('/api/sporthub/follow', { method: 'PUT', body: JSON.stringify(b) }),
  remind: (b: { key: string; title: string; start: number; auto: boolean; channel: { accountId: string; streamId: number; name: string } | null; off?: boolean }) =>
    call<{ reminders: Record<string, unknown> }>('/api/sporthub/remind', { method: 'POST', body: JSON.stringify(b) }),
  pulse: () => call<HubPulse>('/api/sporthub/pulse'),
  onChannel: (accountId: string, streamId: number) => call<{ match: { path: string; eventId: string; title: string } | null; skin?: 'off' | 'score' | 'full' | 'custom'; parts?: { score: boolean; strip: boolean; ticker: boolean; panel: boolean }; custom?: { score: boolean; strip: boolean; ticker: boolean; panel: boolean } | null; hideScores?: boolean; ticker?: { kind: string; label: string; items: { key: string; text: string; score?: string; fav?: boolean }[] }[] }>(`/api/sporthub/on-channel?accountId=${encodeURIComponent(accountId)}&streamId=${streamId}`),
  highlights: () => call<{ hero: HubVideo | null; mine: HubVideo[]; latest: HubVideo[]; hideScores: boolean }>('/api/sporthub/highlights'),
  youtube: () => call<{ sources: { id: string; label: string; leagues: string[] }[]; custom: boolean }>('/api/sporthub/youtube'),
  saveYoutube: (sources: { id: string; label: string; leagues: string[] }[] | null) => call<{ sources: { id: string; label: string; leagues: string[] }[]; custom: boolean }>('/api/sporthub/youtube', { method: 'PUT', body: JSON.stringify({ sources }) }),
};

/** Uitslagen verbergen (geldt per profiel, ook in de apps). */
let hide: boolean | null = null;
const listeners = new Set<(v: boolean) => void>();
export function setHideScores(v: boolean) {
  hide = v;
  listeners.forEach((f) => f(v));
}
export function useHideScores(): boolean {
  const [v, setV] = useState(hide ?? false);
  useEffect(() => {
    listeners.add(setV);
    if (hide === null) hubApi.follow().then((f) => setHideScores(f.hideScores)).catch(() => undefined);
    return () => { listeners.delete(setV); };
  }, []);
  return v;
}

/** Clubkleur als CSS-kleur (ESPN geeft "e2001a"), met een rustige reserve. */
export function teamColor(s: HubSide | null | undefined, fallback = '#2a3140'): string {
  const c = s?.color;
  return c && /^[0-9a-f]{6}$/i.test(c) && c.toLowerCase() !== 'ffffff' && c !== '000000' ? `#${c}` : fallback;
}

export const statusText = (c: HubCard) => (c.state === 'in' ? (c.minute ?? c.detail ?? 'Live') : c.state === 'post' ? (c.detail || 'Afgelopen') : '');
